import { z } from 'zod';
import { IndStatus } from '@api/Entities/Ind/Ind';
import type { IndUpdateDTO } from '@api/DTO/Ind/Ind/dto';
import type { StatusSourceInitialValues } from './types';

import { buildDefaults, createFormModule, mapObject, valueToUndefined } from '@app/Utilities/Form';

/**
 * Zod schema validating UI-facing values for the Status / Source form.
 *
 * Notes:
 * - Allows empty strings for select fields to support controlled inputs.
 * - Validates form state, not API payload semantics.
 */
const schema = z.object({
  /**
   * IND status value.
   *
   * Accepts either a valid enum value or an empty string representing
   * "no selection" at the UI level.
   */
  indStatus: z.union([z.nativeEnum(IndStatus), z.literal('')]),

  /**
   * Selected source identifier.
   *
   * Must be a positive integer when provided.
   * Empty string represents "no selection" in the UI.
   */
  indSource: z.union([z.number().int().positive(), z.literal('')]),

  /** Free-text referral source. */
  indReferral: z.string(),

  /** Free-text referral target. */
  indReferralTo: z.string(),
});

/**
 * Canonical base defaults for the Status / Source form.
 *
 * Rules:
 * - Must include every key in {@link StatusSourceValues}
 * - Should remain immutable
 * - Represents initial UI state when no data is provided
 */
const baseDefaults: StatusSourceValues = {
  indStatus: IndStatus.Active,
  indSource: '',
  indReferral: '',
  indReferralTo: '',
};

/**
 * Status Source form module.
 *
 * This module defines the complete contract for the Status / Source form:
 * - schema: UI-facing validation
 * - baseDefaults: canonical default state
 * - defaults(): merges partial initial values into a complete form state
 * - mapper(): converts form values into an API DTO
 */
export const statusSourceForm = createFormModule({
  schema,
  baseDefaults,

  /**
   * Builds a complete default value set for the form by merging partial
   * initial values over canonical base defaults.
   *
   * @param initial Partial initial values (e.g. from API or parent state)
   */
  defaults: (initial?: Partial<StatusSourceInitialValues>) => buildDefaults(baseDefaults, initial),

  /**
   * Maps validated form values into an API-ready DTO.
   *
   * Responsibilities:
   * - Convert UI placeholders (e.g. empty strings) into `undefined`
   * - Produce a payload suitable for patch-style updates
   *
   * @param values Validated form values
   * @returns DTO suitable for submission to the update endpoint
   */
  mapper: (values): IndUpdateDTO =>
    mapObject(values, {
      indStatus: (v) => valueToUndefined(v.indStatus),
      indSource: (v) => valueToUndefined(v.indSource),
      indReferral: (v) => valueToUndefined(v.indReferral),
      indReferralTo: (v) => valueToUndefined(v.indReferralTo),
    }),
});

/**
 * UI-facing form values type inferred from the schema.
 *
 * Used by:
 * - Form components
 * - Default builders
 * - Mapper functions
 *
 * This type should never be used directly as an API payload.
 */
export type StatusSourceValues = z.infer<typeof schema>;
